import { getApiBaseUrl, getWebAgentBaseUrl } from '../api';
import { aidesApiSlice, categoryType } from '../aides/aidesApiSlice';
import { agentApiSlice } from '../agent/agentApiSlice';
import type { AppDispatch } from '../../store';
import type { WebAgentExecuteResult, WebAgentSpec, WebAgentStepLogEntry } from './webAgentTypes';
import { buildActiveWebAgentConfigQuery, buildWebAgentSpec, isWebAgentAgent } from './webAgentUtils';

export interface WebAgentStreamStepEvent {
  type: 'step';
  step: WebAgentStepLogEntry;
  log: string;
  screenshot?: string;
}
export interface WebAgentStreamDoneEvent { type: 'done'; result: WebAgentExecuteResult }
export interface WebAgentStreamErrorEvent { type: 'error'; message: string }
export type WebAgentStreamEvent = WebAgentStreamStepEvent | WebAgentStreamDoneEvent | WebAgentStreamErrorEvent;

export interface StreamWebAgentExecuteParams {
  spec: WebAgentSpec;
  inputs?: Record<string, unknown>;
  /**
   * Platform API base URL (see `getApiBaseUrl`). The runner uses it, with the forwarded bearer token, to
   * read the secrets that steps reference as `${credential.<id>}`. Secrets are deliberately never sent
   * from here: the credentials token endpoint is called server-side only.
   */
  apiBase?: string;
  token?: string | null;
  /**
   * Keep the browser alive for the next message instead of closing it with the run, so a live converse
   * signs in once. The result then carries a `sessionHandle`.
   */
  keepSession?: boolean;
  /** The previous run's `sessionHandle`, to carry on in that same browser. Empty opens a fresh one. */
  sessionHandle?: string;
  captureFrames?: boolean;
  onEvent: (event: WebAgentStreamEvent) => void | Promise<void>;
  onError?: (error: Error) => void;
  signal?: AbortSignal;
}

function parseSSELines<E extends { type: string }>(buffer: string): E[] {
  const events: E[] = [];
  for (const line of buffer.split(/\n+/)) {
    const trimmed = line.trim();
    if (!trimmed.startsWith('data:')) continue;
    const payload = trimmed.slice(5).trim();
    if (!payload) continue;
    try {
      const parsed = JSON.parse(payload);
      if (parsed?.type) events.push(parsed as E);
    } catch {
      /* ignore malformed line */
    }
  }
  return events;
}

/** Forwards events one at a time, each after the previous handler settles, so their order holds. */
function forwardInOrder<E>(events: E[], onEvent: (event: E) => void | Promise<void>): Promise<void> {
  return events.reduce<Promise<void>>((previous, event) => previous.then(() => onEvent(event)), Promise.resolve());
}

/** Reads an SSE `POST` response body and forwards each `data:` event to `onEvent`. */
async function pumpSSE<E extends { type: string }>(
  response: Response,
  onEvent: (event: E) => void | Promise<void>,
): Promise<void> {
  const reader = response.body?.getReader();
  if (!reader) throw new Error('Streaming is not supported in this environment.');
  const decoder = new TextDecoder();
  let pending = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    pending += decoder.decode(value, { stream: true });
    const lastNewline = pending.lastIndexOf('\n');
    if (lastNewline >= 0) {
      const complete = pending.slice(0, lastNewline + 1);
      pending = pending.slice(lastNewline + 1);
      await forwardInOrder(parseSSELines<E>(complete), onEvent);
    }
  }
  if (pending) await forwardInOrder(parseSSELines<E>(pending), onEvent);
}

const RUNNER_MEDIA_PATH = /^\/web-agent\/media\/[0-9a-f-]+$/i;

/** Shared auth GET for a token-bound runner media reference. */
async function fetchRunnerMediaResponse(mediaUrl: string, token?: string | null): Promise<Response | null> {
  if (!RUNNER_MEDIA_PATH.test(mediaUrl) || !token) return null;
  const host = getWebAgentBaseUrl();
  if (!host) return null;
  try {
    const response = await fetch(`${host}${mediaUrl}`, { headers: { Authorization: `Bearer ${token}` } });
    return response.ok ? response : null;
  } catch {
    return null;
  }
}

/** Fetch one token-bound runner media reference as a File (used for S3 upload without re-embedding base64). */
export async function fetchWebAgentMediaFile(
  mediaUrl: string,
  filename: string,
  contentType?: string,
  token?: string | null,
): Promise<File | null> {
  const response = await fetchRunnerMediaResponse(mediaUrl, token);
  if (!response) return null;
  try {
    const blob = await response.blob();
    if (!blob.size) return null;
    const type = contentType || blob.type || 'application/octet-stream';
    const name = filename || 'attachment';
    if (typeof File === 'function') {
      try {
        const file = new File([blob], name, { type });
        // Some jsdom File shims omit Blob methods; fall through to the Blob wrapper below.
        if (typeof (file as Blob).arrayBuffer === 'function') return file;
      } catch {
        /* fall through */
      }
    }
    // jsdom / older runtimes may lack a usable File; Blob is enough for S3 PUT helpers.
    return Object.assign(blob, { name, lastModified: Date.now() }) as File;
  } catch {
    return null;
  }
}

/** Fetch one token-bound binary reference and recreate the legacy raw-base64 value for live frames. */
async function fetchRunnerMediaBase64(mediaUrl: string, token?: string | null): Promise<string | undefined> {
  const response = await fetchRunnerMediaResponse(mediaUrl, token);
  if (!response) return undefined;
  try {
    const bytes = new Uint8Array(await response.arrayBuffer());
    if (bytes.length === 0) return undefined;
    let binary = '';
    const chunk = 0x2000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
  } catch {
    return undefined;
  }
}

/**
 * Hydrate only live step screenshots so the converse preview keeps working.
 *
 * Do NOT fetch attachment/final-result media here: `done.extracted` must reach the UI as soon as the
 * SSE event arrives. The converse uploads attachments afterward via `fetchWebAgentMediaFile` (for
 * PATCH `s3_keys` only) — those bytes are not needed to render the claim JSON.
 */
async function hydrateRunnerMedia<E extends { type: string }>(event: E, token?: string | null): Promise<E> {
  const hydrated: any = { ...event };
  if (typeof hydrated.screenshot === 'string' && RUNNER_MEDIA_PATH.test(hydrated.screenshot)) {
    const screenshot = await fetchRunnerMediaBase64(hydrated.screenshot, token);
    if (screenshot) hydrated.screenshot = screenshot;
    else delete hydrated.screenshot;
  }
  return hydrated as E;
}

interface RunnerStreamOptions<E extends { type: string }> {
  path: string;
  body: Record<string, unknown>;
  /** Prefix for HTTP/network failures, e.g. "Web agent stream". */
  label: string;
  token?: string | null;
  /** Message to report instead of streaming, when a precondition is unmet. */
  blockedBy?: string;
  onEvent: (event: E | WebAgentStreamErrorEvent) => void | Promise<void>;
  onError?: (error: Error) => void;
  signal?: AbortSignal;
}

/**
 * POST to the Web Agent runner and forward its SSE events. Reports every failure (unconfigured host,
 * bad status, network error) as an `error` event so callers only handle one channel; an aborted stream
 * is silent by design.
 */
async function runnerStream<E extends { type: string }>(options: RunnerStreamOptions<E>): Promise<void> {
  const { path, body, label, token, blockedBy, onEvent, onError, signal } = options;

  const fail = async (message: string): Promise<void> => {
    await onEvent({ type: 'error', message });
    onError?.(new Error(message));
  };

  const host = getWebAgentBaseUrl();
  if (!host) return fail('Web Agent runner URL is not configured. Set CTY_WEB_AGENT_API_URL in the environment.');
  if (blockedBy) return fail(blockedBy);

  try {
    const response = await fetch(`${host}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      ...(signal ? { signal } : {}),
    });
    if (!response.ok) throw new Error(`${label} failed: ${response.status}`);
    await pumpSSE<E>(response, onEvent);
  } catch (error) {
    const err = error as Error;
    if (err?.name === 'AbortError') return;
    await fail(err?.message ?? `${label} failed`);
  }
}

export async function streamWebAgentExecute(params: StreamWebAgentExecuteParams): Promise<void> {
  const { spec, inputs, apiBase, token, keepSession, sessionHandle, captureFrames, onEvent, onError, signal } = params;
  return runnerStream<WebAgentStreamEvent>({
    path: '/web-agent/execute/stream',
    label: 'Web agent stream',
    body: {
      spec,
      inputs: inputs ?? {},
      apiBase: apiBase ?? getApiBaseUrl(),
      ...(keepSession ? { keepSession: true } : {}),
      ...(sessionHandle ? { sessionHandle } : {}),
      ...(captureFrames ? { captureFrames: true } : {}),
      mediaMode: 'reference',
    },
    blockedBy: token?.trim() ? undefined : 'Authentication required. Please sign in and try again.',
    token,
    onEvent: async (event) => onEvent(await hydrateRunnerMedia(event, token)),
    onError,
    signal,
  });
}

/**
 * Runs a web agent to completion through `streamWebAgentExecute` and resolves with its final
 * result, for callers that only need the outcome (not the live steps).
 */
export async function runWebAgent(params: Pick<StreamWebAgentExecuteParams, 'spec' | 'inputs' | 'token' | 'signal'>): Promise<WebAgentExecuteResult> {
  const outcome: { result?: WebAgentExecuteResult; failure?: string } = {};
  await streamWebAgentExecute({
    ...params,
    onEvent: (event) => {
      if (event.type === 'done') outcome.result = event.result;
      else if (event.type === 'error') outcome.failure = event.message;
    },
  });
  const { result, failure } = outcome;
  if (failure) throw new Error(failure);
  if (!result) throw new Error('Web agent run ended without a result.');
  if (result.status === 'error') throw new Error(result.error?.message ?? 'Web agent run failed.');
  return result;
}

/** Runs a one-off RTK query and releases its cache subscription once it settles. */
async function queryOnce<T>(subscription: { unwrap: () => Promise<T>; unsubscribe: () => void }): Promise<T> {
  try {
    return await subscription.unwrap();
  } finally {
    subscription.unsubscribe();
  }
}

/**
 * Finds a web agent by `code` in the agent list the Agents page uses and builds its runnable spec
 * from the active `agent_config`, the same way the Web Agent converse view does.
 */
export async function loadWebAgentSpecByCode(dispatch: AppDispatch, code: string): Promise<WebAgentSpec> {
  const response: any = await queryOnce(
    dispatch(aidesApiSlice.endpoints.getAiWorkersByCategory.initiate({ category: categoryType.AGENT })),
  );
  // The list arrives as `{ agents: [...] }` (a bare array on older APIs), as elsewhere in the app.
  const agents: any[] = response?.agents ?? (Array.isArray(response) ? response : []);
  const agent = agents.find((item) => item?.code === code && isWebAgentAgent(item));
  if (!agent?.aiworker_id) throw new Error(`Web agent "${code}" was not found.`);

  const config = await queryOnce(
    dispatch(agentApiSlice.endpoints.getAiWorkerConfigurations.initiate(buildActiveWebAgentConfigQuery(agent.aiworker_id) as any)),
  );
  return buildWebAgentSpec(agent, config?.configurations?.[0]?.config);
}

/**
 * Release the browser a live converse has been reusing, so it is not held until the runner's idle
 * timeout. Best-effort by design: it runs from an unmount, where a rejection has nowhere to go.
 */
export async function closeWebAgentSession(params: { sessionHandle: string; token?: string | null }): Promise<void> {
  const host = getWebAgentBaseUrl();
  if (!host || !params.sessionHandle) return;
  try {
    await fetch(`${host}/web-agent/session/close`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(params.token ? { Authorization: `Bearer ${params.token}` } : {}),
      },
      body: JSON.stringify({ sessionHandle: params.sessionHandle }),
      // The page may be unloading; ask the browser to send this anyway.
      keepalive: true,
    });
  } catch {
    /* the runner evicts idle sessions on its own */
  }
}

/**
 * LLM config forwarded to the runner so it can call the shared `/api/v3/integrations` endpoint. As with
 * execute, the bearer token is not part of this payload: it travels in the `Authorization` header and the
 * runner reads it from there.
 */
export interface WebAgentBuildLLMConfig {
  /** Platform API base URL (see `getApiBaseUrl`). */
  apiBase: string;
  /** source_id of the shared "Call LLM Port key" action (see `resolveCallLLMActionSourceId`). */
  sourceId: string;
  model: string;
}

/** Result returned by the build agent: what to generate and how to merge it into the editor. */
export interface WebAgentBuildResult {
  /** `question` means the agent paused and needs the user to answer `question` before it can continue. */
  target: 'steps' | 'custom' | 'question';
  /** One of `append` | `replace` | `range` | `single` (string-typed for forward compatibility). */
  mode: string;
  from?: number;
  to?: number;
  entries: any[];
  summary: string;
  /** Set when the agent needs a missing detail (credentials, a value to type, more context). */
  question?: string;
}

export interface WebAgentBuildDoneEvent { type: 'done'; result: WebAgentBuildResult }
export type WebAgentBuildStreamEvent = WebAgentStreamStepEvent | WebAgentBuildDoneEvent | WebAgentStreamErrorEvent;

export interface StreamWebAgentBuildParams {
  prompt: string;
  startUrl?: string;
  /** Configured per-run input fields the agent can template as `${name}` (e.g. `[{ name: 'claimNumber' }]`). */
  inputs?: Array<Record<string, unknown>>;
  existingSteps?: any[];
  existingCustom?: any[];
  llm: WebAgentBuildLLMConfig;
  token?: string | null;
  captureFrames?: boolean;
  onEvent: (event: WebAgentBuildStreamEvent) => void | Promise<void>;
  onError?: (error: Error) => void;
  signal?: AbortSignal;
}

export async function streamWebAgentBuild(params: StreamWebAgentBuildParams): Promise<void> {
  const { prompt, startUrl, inputs, existingSteps, existingCustom, llm, token, captureFrames, onEvent, onError, signal } = params;
  return runnerStream<WebAgentBuildStreamEvent>({
    path: '/web-agent/build/stream',
    label: 'Web agent build',
    body: {
      prompt,
      startUrl: startUrl ?? '',
      inputs: inputs ?? [],
      existingSteps: existingSteps ?? [],
      existingCustom: existingCustom ?? [],
      llm,
      ...(captureFrames ? { captureFrames: true } : {}),
      mediaMode: 'reference',
    },
    blockedBy: llm?.apiBase && llm?.sourceId ? undefined : 'LLM configuration is unavailable. Please try again in a moment.',
    token,
    onEvent: async (event) => onEvent(await hydrateRunnerMedia(event, token)),
    onError,
    signal,
  });
}
